import axios, { CancelTokenSource } from 'axios'
import { useContext, useEffect, useRef, useState } from 'react'
import { useSnackbar } from 'react-simple-snackbar'

import { AddContactIcon, ServiceDeskIcon } from '../../../../components/icon'
import NotificationContactForm from '../../../../components/notification-contact-form'
import Sidepanel from '../../../../components/sidepanel'
import { AppContext } from '../../../../contexts/app-context'
import { MobileOnly } from '../../../../shared-styles/responsive.styles'
import { SIDEPANEL_SUCCESS_OPTS } from '../../../../utils/constants'
import { SD_ROLE } from '../../../../utils/helpers'
import useFolderContacts from '../../../../utils/useFolderContacts'
import HeaderContext from '../../sections/header/context'
import NotificationContactsList from './../../../../components/notification-contacts-list/index'
import {
  AddContactButton,
  NotificationContainer,
  NotificationDesktopButtonContainer,
  NotificationHeader,
  NotificationHeaderContainer,
  NotificationMobileButtonContainer,
  NotificationMobileButtonContainerMinimized,
} from './contacts.styles'

const ContactsModal = ({ selectedLocation }: { selectedLocation: IFolderType }) => {
  const _isMounted = useRef(true)
  const folderId = selectedLocation.id
  const { user } = useContext(AppContext)
  const { showContactsModal, setShowContactsModal, setShowBackButton } = useContext(HeaderContext)
  const [openSnackbar, closeSnackbar] = useSnackbar(SIDEPANEL_SUCCESS_OPTS)
  const { contacts, receiveAlertsCount, userCount, loading, fetchUsers } = useFolderContacts({ folderId })

  // This allow us to cancel an axios call in the useEffect cleanup function when unmounting the component
  const [source] = useState<CancelTokenSource>(axios.CancelToken.source())
  const [editUserInfo, setEditUserInfo] = useState<INotificationContactType | undefined>()
  const [showContactsFormModal, setShowContactsFormModal] = useState<boolean>(false)
  const [selectedServiceDesk, setSelectedServiceDesk] = useState<boolean>(false)

  const hasServiceDesk = !!contacts?.users?.find(contact => contact.user.role.name === SD_ROLE)

  // Clean up when the component unmounts
  useEffect(() => {
    return () => {
      source.cancel('cancelled') // clean up axios calls when component unmounts
      _isMounted.current = false // used to handle memory leaks when performing a state change when the component has already unmounted
    }
  }, [source])

  const onDismiss = () => {
    if (_isMounted.current) {
      setShowContactsModal(false)
      setShowBackButton(false)
      setEditUserInfo(undefined)
      closeSnackbar()
    }
  }

  const onBack = (message: string) => {
    setEditUserInfo(undefined)
    setShowContactsFormModal(false)
    if (message) {
      openSnackbar(message)
      fetchUsers()
    }
  }

  const startEditing = (type: 'service-desk' | 'contact') => {
    setSelectedServiceDesk(type === 'service-desk')
    setShowContactsFormModal(true)
  }

  const startEditingServiceDesk = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault()
    startEditing('service-desk')
  }

  const startEditingContact = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault()
    startEditing('contact')
  }

  const bothButtons = (
    <>
      {!hasServiceDesk && (
        <AddContactButton onClick={startEditingServiceDesk}>
          <ServiceDeskIcon />
          <div>Add Service Desk</div>
        </AddContactButton>
      )}
      <AddContactButton onClick={startEditingContact}>
        <AddContactIcon />
        <div>Add Person</div>
      </AddContactButton>
    </>
  )

  return (
    <Sidepanel
      title="Contacts"
      showTitle={!showContactsFormModal}
      openModal={showContactsModal}
      onDismiss={onDismiss}
      showBackButton={showContactsFormModal}
      onBack={onBack}
    >
      {!showContactsFormModal && (
        <>
          <NotificationContainer>
            <NotificationHeaderContainer>
              <NotificationHeader aria-hidden="true">Contacts</NotificationHeader>
              {contacts?.canAddUsers && (
                <>
                  <NotificationDesktopButtonContainer>{bothButtons}</NotificationDesktopButtonContainer>
                  <NotificationMobileButtonContainerMinimized>
                    {!hasServiceDesk && (
                      <AddContactButton onClick={startEditingServiceDesk} aria-label="Add Service Desk">
                        <ServiceDeskIcon />
                      </AddContactButton>
                    )}
                    <AddContactButton onClick={startEditingContact} aria-label="Add Person">
                      <AddContactIcon />
                    </AddContactButton>
                  </NotificationMobileButtonContainerMinimized>
                </>
              )}
            </NotificationHeaderContainer>
            <NotificationContactsList
              contacts={contacts?.users ?? []}
              onSelectContact={contact => {
                setEditUserInfo(contact)
                startEditing('contact')
              }}
              loading={loading}
            />
            {contacts?.canAddUsers && (
              <MobileOnly>
                <NotificationMobileButtonContainer>{bothButtons}</NotificationMobileButtonContainer>
              </MobileOnly>
            )}
          </NotificationContainer>
        </>
      )}
      {showContactsFormModal && user && contacts && (
        <NotificationContactForm
          onBack={onBack}
          user={user}
          folder={selectedLocation}
          editUserInfo={editUserInfo}
          canAddUsers={contacts.canAddUsers}
          serviceDeskContact={selectedServiceDesk}
          receiveAlertsCount={receiveAlertsCount}
          userCount={userCount}
        />
      )}
    </Sidepanel>
  )
}

export default ContactsModal
